import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {ScaleLinear} from 'd3';
import React from 'react';

import {Part} from 'biojs/Part';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import {LayeringMode, PolymerLayout} from 'constants/MapsConstants';
import FileStates from 'FileStates';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkAnnotation,
  PART_ANNOTATION_ARCHIVED_OPACITY,
  LINK_NAME_FONT_SIZE,
  SINGLE_ANNOTATION_HEIGHT,
  SINGLE_ANNOTATION_RADIUS,
  HoveredLinkIndices,
  LINK_ANNOTATION_GAP,
} from 'molbio/maps/BiointelligentLinksConstants';
import {getAnnotationLabelData, AnnotationRenderParams} from 'molbio/maps/linear/AnnotationsHelpers';
import BiointelligentLinkAnnotationInnerDetails from 'molbio/maps/linear/BiointelligentLinkAnnotationInnerDetails';
import {
  getLinkAnnotationColor,
  isPartLinkType,
  modelsToLinkAnnotations,
} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import {LayersDict, timesPerSlice, whichDisplay} from 'molbio/maps/linear/LayeringHelpers';
import Type from 'molbio/maps/Type';
import {TranscriptionMetadata} from 'molbio/TranscriptionsUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';

function getAnnotationRenderParams(
  /** The link being rendered */
  link: BiointelligentLinkAnnotation,
  /** A Type object; the type for the annotation label */
  type: Type,
  /** The index at which the link rendering starts */
  start: number,
  /** The index at which the link rendering ends */
  end: number,
  /** The width in pixels of the annotation */
  dX: number,
  /** The height in pixels of the annotation */
  dY: number
): AnnotationRenderParams {
  const outOfSync = !link.isInSync;
  const isArchived = link.isArchived;
  return {
    start,
    end,
    width: Math.max(dX - LINK_ANNOTATION_GAP, 0),
    height: dY,
    tipWidth: Math.min(dX, type.width(1)),
    arrowsOffset: 6,
    arrowWidth: 3,
    arrowSpacing: 3,
    color: getLinkAnnotationColor(link),
    labelColor: isArchived ? Colors.NEAR_BLACK : Colors.WHITE,
    outsideLabelColor: Colors.NEAR_BLACK,
    forceOpacity: isArchived ? PART_ANNOTATION_ARCHIVED_OPACITY : 1.0,
    forceName: link.name + (isArchived ? ' (archived)' : ''),
    outOfSync,
  };
}

interface BiointelligentLinkAnnotationBaseProps {
  /** A function that takes the index of a residue and returns its x-axis position on the slice */
  xFn: ScaleLinear<number, number>;
  /** A function that takes the y-index of a link annotation and returns its y-axis position on the slice */
  yFn: ScaleLinear<number, number>;
  /** The index at which the slice starts */
  sliceStart: number;
  /** The index at which the slice ends */
  sliceEnd: number;
  /** In the minimap, the residue index at the LHS of the viewport */
  leftBP: number | null;
  /** In the minimap, the residue index at the RGS of the viewport */
  rightBP: number | null;
  /** The width in pixels of the slice */
  visibleWidth: number;
  /** The Backbone sequence being rendered */
  sequence: Sequence;
  /** A Type model; the type of the residues being annotated */
  residuesType: Type;
  /** A list of context menu option names to display when a link annotation is right-clicked */
  contextMenuOptions: Array<string>;
  /** A function that triggers a redraw of the slice wrapper. */
  redraw: () => void;
  /** The ordinal y-indexes of the features on this layer. */
  layersDict: LayersDict;
  /** How to layer features. */
  layeringMode: LayeringMode;
  /** Callback so other slice views can react when a user hovers over a link */
  setHoveredLinkIndices: (indices: HoveredLinkIndices | null) => void;
}

interface BiointelligentLinkAnnotationSegmentProps extends BiointelligentLinkAnnotationBaseProps {
  link: BiointelligentLinkAnnotation;
  type: Type;
  linkSegmentIndex: number;
}

export function BiointelligentLinkAnnotationSegment({
  link,
  xFn,
  yFn,
  sliceStart,
  sliceEnd,
  leftBP,
  rightBP,
  visibleWidth,
  sequence,
  residuesType,
  contextMenuOptions,
  type,
  layersDict,
  layeringMode,
  linkSegmentIndex,
  setHoveredLinkIndices,
}: BiointelligentLinkAnnotationSegmentProps): JSX.Element {
  const {start, end} = whichDisplay(
    sliceStart,
    sliceEnd,
    link.drawStart ?? link.start,
    link.drawEnd ?? link.end,
    linkSegmentIndex,
    sequence.length()
  )!;

  const params = getAnnotationRenderParams(
    link,
    type,
    start,
    end,
    xFn(end) - xFn(start),
    SINGLE_ANNOTATION_HEIGHT
  );
  const translateX = xFn(start);
  const translateY = yFn(layersDict[link.layerIndex]);

  getAnnotationLabelData(
    params,
    type,
    residuesType,
    link.name,
    0,
    layeringMode,
    visibleWidth,
    leftBP,
    rightBP,
    2,
    'none'
  );

  return (
    <g
      className={classNames(css(styles.pointer), 'layer-group')}
      onMouseEnter={() => {
        setHoveredLinkIndices({
          start: link.start,
          end: link.end,
          // Part links always highlight on both strands.
          highlightedStrand: isPartLinkType(link.linkType) ? 0 : link.direction,
        });
      }}
      onMouseLeave={() => {
        setHoveredLinkIndices(null);
      }}
      onMouseDown={(e) => {
        link.onClick(sequence, e.shiftKey);
      }}
      onDoubleClick={(e) => {
        link.onDoubleClick(sequence, e.shiftKey);
      }}
      onContextMenu={(e) => {
        link.onClick(sequence, e.shiftKey);
        selectioncontextmenu.open(e.nativeEvent, sequence, contextMenuOptions);
      }}
    >
      <Popover
        renderContent={() => (
          <BioentityPopoverContent
            id={link.linkedEntityId}
            onClickName={() => {
              FileStates.get().openFolderItem(link.linkedEntityId);
            }}
            validationExtras={null}
          />
        )}
      >
        <g
          {...getTestProps({
            component: 'BiointelligentLinkAnnotation',
            element: 'root',
          })}
          transform={`translate(${translateX}, ${translateY})`}
        >
          <rect
            fill={params.color}
            fillOpacity={params.forceOpacity}
            rx={SINGLE_ANNOTATION_RADIUS}
            ry={SINGLE_ANNOTATION_RADIUS}
            height={params.height}
            width={params.width}
          />
          <BiointelligentLinkAnnotationInnerDetails
            linkAnnotation={link}
            sliceStart={sliceStart}
            sliceEnd={sliceEnd}
            type={type}
            annotationWidth={params.width}
            annotationHeight={params.height}
          />
        </g>
      </Popover>
    </g>
  );
}

interface BiointelligentLinkAnnotationProps extends BiointelligentLinkAnnotationBaseProps {
  link: BiointelligentLinkAnnotation;
  type: Type;
}

export function BiointelligentLinkAnnotationView(props: BiointelligentLinkAnnotationProps): JSX.Element {
  const linkSegments = timesPerSlice(
    props.link,
    props.sliceStart,
    props.sliceEnd,
    props.link.drawStart ?? props.link.start,
    props.link.drawEnd ?? props.link.end
  );
  return (
    <>
      {linkSegments.map((linkSegment, i) => (
        <BiointelligentLinkAnnotationSegment key={`${props.link.id}-${i}`} linkSegmentIndex={i} {...props} />
      ))}
    </>
  );
}

export interface BiointelligentLinkAnnotationsProps extends BiointelligentLinkAnnotationBaseProps {
  /** A function that triggers a redraw of the slice wrapper. */
  redraw: () => void;
  /** Flat object layout data */
  layout: PolymerLayout;
  /** The type of link being rendered. */
  linkType: BiointelligentLinkType;
  /** The raw models of the links that appear on this sequence. */
  sliceModels: Array<Part> | Array<TranscriptionMetadata>;
}

export default function BiointelligentLinkAnnotations(
  props: BiointelligentLinkAnnotationsProps
): JSX.Element {
  // Always redraw layer wrapper; it'll account for the number of links
  props.redraw();

  const type = new Type(LINK_NAME_FONT_SIZE);

  const {sequence: containerSequence, layeringMode, sliceModels: models, linkType} = props;
  const sliceBiointelligentLinks = modelsToLinkAnnotations({
    containerSequence,
    layeringMode,
    models,
    linkType,
  });

  return (
    <svg>
      <ComponentWrapper layout={props.layout} shouldStopMouseDownPropagation={true}>
        <>
          {sliceBiointelligentLinks.map((link, i) => (
            <BiointelligentLinkAnnotationView key={link.id} link={link} type={type} {...props} />
          ))}
          ;
        </>
      </ComponentWrapper>
    </svg>
  );
}

const styles = StyleSheet.create({
  pointer: {
    cursor: 'pointer',
  },
});
